import merge from 'lodash/merge';
import noop from 'lodash/noop';
import { call, put, select } from 'redux-saga/effects';

import * as AppActions from '../../../actions';
import * as AppConstants from '../../../constants';
import { getInDomain } from '../../../domains/externalUtils';
import * as AppErrors from '../../../lib/error';
import { putAndWait } from '../../../sagas/TaskManager';
import Logger from '../../../tools/Logger';
import * as AppUtils from '../../../utils';

const log = Logger.child({ component: 'mediator/useCases/skipTrackForward' });

export function* buildSkipTrackForwardDTO(action) {
  return merge(
    {
      data: {
        track: yield select(getInDomain('playback.utils.getCurrentAudioTrackFromActivePlaylist')),
        type: yield select(getInDomain('playback.utils.getCurrentPlaylistType')),
      },
    },
    action,
  );
}

// eslint-disable-next-line require-yield
export function* isTrackUnskippable(action) {
  const { track } = action.data;

  // Some tracks simply don't have rights to skip at all
  if (!track || !track.get('allowSkip')) {
    return true;
  }

  return false;
}

export function* isTrackFreeSkip(action) {
  const { track, sourceId } = action.data;

  // No need to check anything else if track has rights to freely skip
  if (AppUtils.Playlist.doesTrackAllowLimitlessSkips(track)) {
    return true;
  }

  // Other tracks don't have rights to skip only after the skip limit has been reached
  const hasListenerReachedAnySkipLimits = yield select(
    AppUtils.Skip.hasListenerReachedAnySkipLimits,
    sourceId,
  );
  const doesTrackAllowSkipAfterLimit = AppUtils.Playlist.doesTrackAllowSkipAfterLimit(track);
  if (hasListenerReachedAnySkipLimits && !doesTrackAllowSkipAfterLimit) {
    return false;
  }

  return false;
}

export function* handleUnskippableTrack(action) {
  const miniCoachmarkType = yield select(
    AppUtils.Skip.getUnskippableTrackCoachmarkType,
    action.type,
  );
  yield put(AppActions.MiniCoachmark.openMiniCoachmark(miniCoachmarkType));
  throw new AppErrors.ShortCircuitError('Track was not skippable');
}

// eslint-disable-next-line require-yield
export function* handleTrackFreeSkip() {
  log.info('handleTrackFreeSkip(): ');
}

export function* handleTrackRewardSkip(action) {
  const { sourceId } = action.data;
  log.info('handleTrackRewardSkip(): ');

  // do not trigger coachmark on station mode change
  const isStationChangeMode =
    action.data.skipType === AppConstants.Playback.FragmentRequestReasons.CHANGE_MODE;

  if (
    !isStationChangeMode &&
    (yield select(AppUtils.Skip.hasListenerReachedAnySkipLimits, sourceId))
  ) {
    if (yield select(AppUtils.Listener.isListenerAdSupportedSkip)) {
      log.info('handleTrackRewardSkip(): skip limit reached for ad supported listener');
      yield call(putAndWait, {
        type: AppConstants.Ads.Rewards.ActionTypes.SYNC_SKIP_REWARDS,
        data: {
          actionType: action.type,
          parentAction: action.data.parentAction,
          pandoraId: action.data.track.get('pandoraId'),
        },
      });
    } else {
      log.info('handleTrackRewardSkip(): skip limit reached T2/T3');
      const miniCoachmarkType = yield select(
        AppUtils.Skip.getUnskippableTrackCoachmarkType,
        action.type,
      );
      yield put(AppActions.MiniCoachmark.openMiniCoachmark(miniCoachmarkType));
      throw new AppErrors.ShortCircuitError('Track was not skippable');
    }

    yield put(getInDomain('analytics.actions.logSkipLimit', noop)());
  }
}

export function* handleSpecialStation(action) {
  const { sourceId } = action.data;

  log.info('handleSpecialStations():');
  const isThumbprintStationId = yield select(
    getInDomain('playback.utils.isThumbprintStationId'),
    sourceId,
  );
  const isThumbprintProcessSkips = yield select(
    getInDomain('playback.utils.isThumbprintProcessSkips'),
    sourceId,
  );
  // if thumbprint radio skip is a signal to get a new playlist
  if (isThumbprintStationId && isThumbprintProcessSkips) {
    // eslint-disable-next-line require-atomic-updates
    action.data.forceNewPlaylist = true;
  }
}

export function* handleSpecialTracks(action) {
  const { sourceId, track } = action.data;

  log.info('handleSpecialTracks():');
  if (AppUtils.Playlist.isArtistMessage(track)) {
    log.info('skipTrackStation(): registering the skip of a artist message');
    yield put(
      AppActions.Playback.registerArtistMessageMetric(track, AppConstants.Amp.AmpMetrics.SKIP),
    );
  }
  if (AppUtils.VoiceTrack.isStationVoiceTrack(track)) {
    log.info('skipTrackStation(): registering the skip of a voice track');
    yield put(
      AppActions.Playback.registerVoiceTrackMetric(
        sourceId,
        track,
        AppConstants.Amp.AmpMetrics.SKIP,
      ),
    );
  }
}

export function* handleRegisterSkipTrack(action) {
  const { sourceId, track } = action.data;

  log.info('handleRegisterSkipTrack():');
  const doesTrackCountTowardsSkipLimit = !AppUtils.Playlist.doesTrackAllowLimitlessSkips(track);
  if (doesTrackCountTowardsSkipLimit) {
    yield put(AppActions.Playback.registerSkipTrack(sourceId));
  }
}

export function* handlePlayNextStationTrack(action) {
  const { sourceId, forceNewPlaylist } = action.data;

  log.info('handlePlayNextStationTrack(): attempting to play next track in station');
  const isAudioPlaying = yield select(getInDomain('playback.utils.isAudioPlaying'));
  yield call(
    putAndWait,
    getInDomain('playback.actions.playTrackFromStation')({
      id: sourceId,
      nextTrackForceNewPlaylist: forceNewPlaylist,
      logPlaybackActionsAnalytics: !isAudioPlaying,
      userInitiatedSkip: true,
      next: true,
      skipType: action.data.skipType,
    }),
  );
}

export function* handleAdOpportunity(action) {
  const { track, parentAction } = action.data;
  log.info('handleAdOpportunity(): attempting to play next track in station');
  yield call(getInDomain('ads.utils.adOpportunity', null), parentAction || action, track);
}

export function* handleFlexOpportunity(action) {
  const { sourceId } = action.data;

  log.info('handleFlexOpportunity(): notify of track replayability');
  const nextTrack = yield select(getInDomain('playback.utils.getPlaylistCurrentTrack'), sourceId);
  yield call(putAndWait, {
    type: AppConstants.Ads.Rewards.ActionTypes.NOTIFY_USER_OF_TRACK_REPLAYABILITY,
    data: { track: nextTrack },
  });
}

export function* handleBingeCheck(action) {
  const isChangeModeSkip =
    action.data.skipType === AppConstants.Playback.FragmentRequestReasons.CHANGE_MODE;
  const isBingeSkipping = yield select(AppUtils.Listener.isCurrentListenerBingeSkipping);
  // if binge skipping open modal coachmark
  // and the skip wasn't caused by changing modes.
  if (isBingeSkipping && !isChangeModeSkip) {
    log.info('handleBingeCheck(): opening binge skipper coachmark');
    yield put(AppActions.MiniCoachmark.openBingeSkipperCoachmark());
  }
}

export function* logToDRM(action) {
  const { skipType, track } = action.data;

  // Log skip to DRM
  let drmSkipReason = null;
  switch (skipType) {
    case AppConstants.Playback.FragmentRequestReasons.SKIP: {
      drmSkipReason = AppConstants.Playback.DRMSkipReasons.SKIP;
      break;
    }
    case AppConstants.Playback.FragmentRequestReasons.THUMBED_DOWN: {
      drmSkipReason = AppConstants.Playback.DRMSkipReasons.THUMBED_DOWN;
      break;
    }
  }
  log.info(`logToDRM(): logging DRM skip with ${drmSkipReason} reason`);

  yield put(AppActions.Playback.logSkipDRM(track, drmSkipReason));
}

export function* logToStats(action) {
  const { skipType, track, sourceId, type } = action.data;

  // Log skip to Stats
  if (skipType !== AppConstants.Playback.FragmentRequestReasons.THUMBED_DOWN) {
    log.info(`logToStats(): logging track end with ${skipType} skip type`);
    yield put(
      getInDomain(
        'analytics.actions.logTrackEnd',
        noop,
      )({
        reason: 'skipped',
        playlistType: type,
        playlistId: sourceId,
        track,
      }),
    );
  }
}

export function* handlePlayNextAPSTrack(action) {
  log.info('handlePlayNextAPSTrack()');
  const { sourceId, index, modePandoraId } = action.data;

  yield call(
    putAndWait,
    getInDomain('playback.actions.skipItem')({
      sourceId,
      index,
      relativeIndex: 1,
      modePandoraId,
    }),
  );
}
